import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, firstValueFrom, Observable, of, tap } from 'rxjs';
import { ROUTES } from '../shared/constants';
import { IAdminInfo } from '../shared/interfaces/IAdmin';
import { IForgotPassword, IResetPassword, IUserLogin, IVerifyToken } from '../shared/interfaces/IAuth';
import { HttpService } from './http.service';

@Injectable({
  providedIn: 'root'
})
export class AdminService {
  public accessToken: string | null;
  public refreshToken: string | null;
  public adminInfo: IAdminInfo | null;
  private isRefreshingToken = false;
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  private tokenKey = 'admin_gfliId';

  constructor(
    private router: Router,
    private httpService: HttpService) {
    // Load tokens from localStorage on initialization
    const token = localStorage.getItem(this.tokenKey);
    const refreshToken = token?.slice(0, 64);
    const accessToken = token?.slice(64);
    if (refreshToken && accessToken) {
      this.refreshToken = refreshToken;
      this.accessToken = accessToken;
    }
  }

  setToken(accessToken: string, refreshToken?: string): void {
    if (accessToken && refreshToken) {
      this.accessToken = accessToken;
      this.refreshToken = refreshToken;
      localStorage.setItem(this.tokenKey, `${refreshToken}${accessToken}`);
    }
  }

  public clearTokens(): void {
    this.accessToken = null;
    this.refreshToken = null;
    localStorage.removeItem(this.tokenKey);
  }

  setAdminInfo(): Observable<IAdminInfo | null> {
    if (this.adminInfo) return of(this.adminInfo);
    return this.httpService.get('admin');
  }

  async isAuthenticated(): Promise<boolean> {
    if (!this.accessToken) return false;
    this.adminInfo = await firstValueFrom(this.setAdminInfo());
    return !!this.adminInfo;
  }

  getRefreshToken() {
    if (!this.refreshToken) {
      throw new Error('No refresh token available');
    }

    if (this.isRefreshingToken) {
      // If refresh is already in progress, wait for the result
      return new Observable(observer => {
        this.refreshTokenSubject.subscribe({
          next: (token) => {
            if (token) {
              observer.next(token);
              observer.complete();
            }
          },
          error: (err) => {
            observer.error(err);
          }
        });
      });
    } else {
      // Start new refresh
      this.isRefreshingToken = true;
      this.refreshTokenSubject.next(null);

      return this.httpService.post('admin/refresh-token', { refreshToken: this.refreshToken }).pipe(
        // Using tap to handle side effects without affecting the stream
        tap({
          next: (token) => {
            this.isRefreshingToken = false;
            this.refreshTokenSubject.next(token);
          },
          error: (err) => {
            this.isRefreshingToken = false;
            this.refreshTokenSubject.error(err);
            // Reset subject for next attempt
            this.refreshTokenSubject = new BehaviorSubject<any>(null);
          }
        })
      );
    }
  }

  login(payload: IUserLogin) {
    return this.httpService.post('admin/login', payload);
  }

  async logout(apiCall = true) {
    this.clearTokens();
    this.adminInfo = null;
    this.router.navigate([ROUTES.ADMIN.LOGIN]);
    try {
      if (this.refreshToken && apiCall) {
        await firstValueFrom(this.httpService.post('admin/logout', { refreshToken: this.refreshToken }));
      }
    } catch (error) {
      console.error(error);
    }
  }

  resetPassword(payload: IResetPassword) {
    return this.httpService.post('admin/reset-password', payload);
  }

  forgotPassword(payload: IForgotPassword) {
    return this.httpService.post('admin/forgot-password', payload);
  }

  updateUserInfo(payload: IAdminInfo) {
    return this.httpService.put('admin', payload);
  }

  async verifyToken(payload: IVerifyToken): Promise<boolean> {
    return await firstValueFrom(this.httpService.post('admin/verify-token', payload));
  }

  //#region User Admin Management
  getUserAdmin(params: any): Observable<any[]> {
    return this.httpService.get("admin/users", { params })
  }

  addOrUpdateUserAdmin(payload: any, redirectUrl?: string) {
    let params: HttpParams | undefined;
    if (redirectUrl) {
      params = new HttpParams().set('redirectUrl', redirectUrl);
    }
    return this.httpService.post('admin/users', payload, params ? { params } : undefined);
  }

  deleteUserAdmin(id: number) {
    return this.httpService.delete(`admin/users/${id}`);
  }
  //#endregion

  //#region Process Chart
  getProcessLineChart(start: string, end: string): Observable<any[]> {
    const params = new HttpParams()
      .set('start', start)
      .set('end', end);
    return this.httpService.get('admin/process', { params });
  }

  getOverViews(): Observable<any> {
    return this.httpService.get('admin/overview')
  }

  getAuditLogClientCredentials(params: any): Observable<any> {
    return this.httpService.get('admin/client-credential/logs', { params });
  }

  getBackupData(params: any): Observable<any> {
    return this.httpService.get('admin/jobs/backup-data', { params });
  }

  getClientCredentials(params: any): Observable<any> {
    return this.httpService.get('admin/client-credentials', { params });
  }

  createOrUpdateClientCredential(params: any): Observable<any> {
    return this.httpService.post('admin/client-credentials', params);
  }

  updateClientCredential(id: number, params: any): Observable<any> {
    return this.httpService.put(`admin/client-credentials/${id}`, params);
  }

  deleteClientCredential(id: number): Observable<any> {
    return this.httpService.delete(`admin/client-credentials/${id}`);
  }
}
